<template>
  <div class="reports-tab">
    <div class="card">
      <!-- Фильтры -->
      <div class="flex gap-3 mb-4 flex-wrap">
        <div>
          <label class="block text-sm text-600 mb-2">Дата от</label>
          <Calendar
            v-model="filters.date_from"
            dateFormat="dd.mm.yy"
            placeholder="Выберите дату"
            @date-select="loadReports"
            showIcon
          />
        </div>
        <div>
          <label class="block text-sm text-600 mb-2">Дата до</label>
          <Calendar
            v-model="filters.date_to"
            dateFormat="dd.mm.yy"
            placeholder="Выберите дату"
            @date-select="loadReports"
            showIcon
          />
        </div>
        <div class="flex align-items-end">
          <Button
            icon="pi pi-filter-slash"
            label="Сбросить"
            class="p-button-outlined"
            @click="resetFilters"
          />
        </div>
      </div>

      <!-- Таблица отчетов -->
      <DataTable
        :value="reports"
        :loading="loading"
        paginator
        :rows="20"
        responsiveLayout="scroll"
        stripedRows
      >
        <Column field="report_date" header="Дата" style="width: 160px">
          <template #body="slotProps">
            {{ formatDate(slotProps.data.report_date) }}
          </template>
        </Column>
        <Column field="total_products" header="Всего" style="width: 80px">
          <template #body="slotProps">
            <span class="font-semibold">{{ slotProps.data.total_products || 0 }}</span>
          </template>
        </Column>
        
        <!-- ЭТАП 1: Добавление -->
        <Column style="width: 100px">
          <template #header>
            <div class="text-center">
              <div class="text-xs text-gray-500 mb-1">ЭТАП 1</div>
              <div>Добавлено</div>
            </div>
          </template>
          <template #body="slotProps">
            <Tag v-if="slotProps.data.stage1_created > 0" :value="slotProps.data.stage1_created" severity="success" />
            <span v-else class="text-500">0</span>
          </template>
        </Column>
        <Column style="width: 100px">
          <template #header>
            <div class="text-center">
              <div class="text-xs text-gray-500 mb-1">ЭТАП 1</div>
              <div>Ошибки</div>
            </div>
          </template>
          <template #body="slotProps">
            <Tag v-if="slotProps.data.stage1_failed > 0" :value="slotProps.data.stage1_failed" severity="danger" />
            <span v-else class="text-500">0</span>
          </template>
        </Column>
        
        <!-- ЭТАП 2: Обновление -->
        <Column style="width: 100px">
          <template #header>
            <div class="text-center">
              <div class="text-xs text-blue-500 mb-1">ЭТАП 2</div>
              <div>Обновлено</div>
            </div>
          </template>
          <template #body="slotProps">
            <Tag v-if="slotProps.data.stage2_updated > 0" :value="slotProps.data.stage2_updated" severity="info" />
            <span v-else class="text-500">0</span>
          </template>
        </Column>
        <Column style="width: 100px">
          <template #header>
            <div class="text-center">
              <div class="text-xs text-blue-500 mb-1">ЭТАП 2</div>
              <div>Пропущено</div>
            </div>
          </template>
          <template #body="slotProps">
            <Tag v-if="slotProps.data.stage2_skipped > 0" :value="slotProps.data.stage2_skipped" severity="secondary" />
            <span v-else class="text-500">0</span>
          </template>
        </Column>
        <Column style="width: 100px">
          <template #header>
            <div class="text-center">
              <div class="text-xs text-blue-500 mb-1">ЭТАП 2</div>
              <div>Ошибки</div>
            </div>
          </template>
          <template #body="slotProps">
            <Tag v-if="slotProps.data.stage2_failed > 0" :value="slotProps.data.stage2_failed" severity="danger" />
            <span v-else class="text-500">0</span>
          </template>
        </Column>
        
        <!-- Недостающие данные -->
        <Column field="missing_data_count" header="Недост. данные" style="width: 120px">
          <template #body="slotProps">
            <Tag v-if="slotProps.data.missing_data_count > 0" :value="slotProps.data.missing_data_count" severity="warning" />
            <span v-else class="text-500">0</span>
          </template>
        </Column>
        
        <!-- Token -->
        <Column field="file_token" header="Token" style="width: 90px">
          <template #body="slotProps">
            <code v-if="slotProps.data.file_token" class="text-xs">{{ slotProps.data.file_token.substring(0, 8) }}...</code>
            <span v-else class="text-500">-</span>
          </template>
        </Column>
        
        <!-- Действия -->
        <Column header="Действия" style="width: 110px">
          <template #body="slotProps">
            <Button
              icon="pi pi-download"
              label="Скачать"
              class="p-button-sm p-button-text p-button-success"
              :disabled="!slotProps.data.file_token || !slotProps.data.file_path"
              @click="downloadReport(slotProps.data)"
            />
          </template>
        </Column>
      </DataTable>

      <!-- Пустое состояние -->
      <div v-if="!loading && reports.length === 0" class="text-center p-5">
        <i class="pi pi-inbox text-400" style="font-size: 3rem"></i>
        <p class="text-500 mt-3">Отчеты о синхронизации отсутствуют</p>
        <p class="text-400 text-sm">Отчеты создаются автоматически при синхронизации товаров</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useToast } from 'primevue/usetoast'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Button from 'primevue/button'
import Calendar from 'primevue/calendar'
import Tag from 'primevue/tag'
import apiService from '../../services/apiService'

// Props
const props = defineProps({
  storeId: {
    type: Number,
    required: true
  }
})

const toast = useToast()

// State
const loading = ref(false)
const reports = ref([])

const filters = ref({
  date_from: null,
  date_to: null
})

// Загрузка отчетов
const loadReports = async () => {
  loading.value = true
  try {
    const params = { store_id: props.storeId }
    
    if (filters.value.date_from) {
      params.date_from = filters.value.date_from.toISOString().split('T')[0]
    }
    if (filters.value.date_to) {
      params.date_to = filters.value.date_to.toISOString().split('T')[0]
    }
    
    const response = await apiService.get('/sync/products/reports', { params })
    reports.value = response.data
  } catch (error) {
    console.error('Ошибка загрузки отчетов:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось загрузить отчеты',
      life: 3000
    })
  } finally {
    loading.value = false
  }
}

// Сброс фильтров
const resetFilters = () => {
  filters.value = {
    date_from: null,
    date_to: null
  }
  loadReports()
}

// Форматирование даты
const formatDate = (dateString) => {
  if (!dateString) return '—'
  const date = new Date(dateString)
  return date.toLocaleDateString('ru-RU', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// Скачивание отчета
const downloadReport = async (report) => {
  if (!report.file_token) {
    toast.add({
      severity: 'warn',
      summary: 'Предупреждение',
      detail: 'Файл отчета не доступен',
      life: 3000
    })
    return
  }
  
  try {
    const response = await apiService.get(
      `/sync/reports/${report.id}/download?token=${report.file_token}`,
      {
        responseType: 'blob'
      }
    )
    
    // Создаем ссылку для скачивания
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `sync_report_${report.id}.xlsx`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
    
    toast.add({
      severity: 'success',
      summary: 'Успех',
      detail: 'Отчет загружен',
      life: 3000
    })
  } catch (error) {
    console.error('Ошибка скачивания отчета:', error)
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось скачать отчет',
      life: 3000
    })
  }
}

// Отслеживание изменения storeId
watch(() => props.storeId, () => {
  loadReports()
})

onMounted(() => {
  loadReports()
})
</script>

<style scoped>
.reports-tab {
  min-height: 600px;
}

.card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}
</style>
